import { Actions } from '@components/shared/Actions'
import { EyeIcon } from '@assets/svgs/EyeIcon'
import { formattedDatePtBr } from '@utils/dates/formattedDatePtBr'
import { currencyMask } from '@utils/masks/currencyMask'

import type { ColumnDefProps } from '@components/shared/Table/types'

import * as S from '../styles'

export interface CollaboratorItemProps {
  name: string
  registration: string
  position: string
  startDate: string
  status: 'Ativo' | 'Inativo' | 'Férias' | 'Licença Maternidade/Paternidade'
  profitCenter?: string
  salaries: number
  benefits: number
  charges: number
  grandTotal: number
}

interface ParseCollaboratorsGridProps {
  data: CollaboratorItemProps[]
  onSeeMore: (data: CollaboratorItemProps) => void
}

const getInitials = (name: string) => {
  const names = name.trim().split(' ')
  if (names.length === 1) return names[0].charAt(0).toUpperCase()
  return `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`.toUpperCase()
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Ativo':
      return '#34A866'
    case 'Inativo':
      return '#999'
    case 'Férias':
      return '#F5A623'
    case 'Licença Maternidade/Paternidade':
      return '#E91E63'
    default:
      return '#283897'
  }
}

export const parseCollaboratorsGrid = ({ data, onSeeMore }: ParseCollaboratorsGridProps) => {
  if (!data) return []

  const parsedData: ColumnDefProps[][] = data.map((item) => [
    {
      id: 'collaboratorName',
      columnName: 'Nome do Colaborador',
      sortKey: 'employeename',
      headerAlign: 'center',
      value: (
        <S.CollaboratorInfo>
          <S.Avatar>{getInitials(item.name)}</S.Avatar>
          <S.CollaboratorDetails>
            <S.CollaboratorName>{item.name}</S.CollaboratorName>
            <S.CollaboratorRegistration>{item.registration}</S.CollaboratorRegistration>
          </S.CollaboratorDetails>
        </S.CollaboratorInfo>
      ),
      minWidth: '18rem',
      sortable: true,
      withoutTooltip: true
    },
    {
      id: 'position',
      columnName: 'Cargo',
      sortKey: 'position',
      value: item.position || '-',
      align: 'center',
      sortable: true,
      minWidth: '14rem'
    },
    {
      id: 'startDate',
      columnName: 'Data Início',
      sortKey: 'startdate',
      value: item.startDate ? formattedDatePtBr(item.startDate) : '-',
      align: 'center',
      sortable: true,
      minWidth: '10rem'
    },
    {
      id: 'salaries',
      columnName: <span style={{ whiteSpace: 'nowrap' }}>Salários com Adicionais (R$)</span>,
      value: currencyMask(item.salaries ?? 0),
      align: 'right',
      headerAlign: 'center',
      minWidth: '15rem'
    },
    {
      id: 'benefits',
      columnName: 'Benefícios (R$)',
      value: currencyMask(item.benefits ?? 0),
      align: 'right',
      headerAlign: 'center',

      minWidth: '10rem'
    },
    {
      id: 'charges',
      columnName: 'Encargos (R$)',
      value: currencyMask(item.charges ?? 0),
      align: 'right',
      headerAlign: 'center',
      
      minWidth: '10rem'
    },
    {
      id: 'grandTotal',
      columnName: 'Total Geral (R$)',
      value: currencyMask(item.grandTotal ?? 0),
      align: 'right',
      headerAlign: 'center',
      
      minWidth: '10rem'
    },
    {
      id: 'status',
      columnName: 'Status',
      align: 'center',
      value: (
        <S.StatusBadge color={getStatusColor(item.status)}>
          {item.status}
        </S.StatusBadge>
      ),
      
      withoutTooltip: true
    },
    {
      id: 'actions',
      columnName: 'Ações',
      align: 'center',
      minWidth: '5rem',
      maxWidth: '5rem',
      stickyPosition: 'right',
      withoutTooltip: true,
      value: <Actions actions={[{ label: 'Ver mais', icon: <EyeIcon />, onClick: () => onSeeMore(item) }]} />
    }
  ])

  return parsedData
}
